// DOC HELPER — ORC-02 projection-only UI boundary.
// Field/stage labels rendered here must come from backend orchestration_status.
// Do not add business-specific field label fallback maps in the frontend.

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function isAuthoritativeQuestionComposerChooser(payload = {}) {
  const source = resolveObject(payload);
  const primaryFlow = resolveObject(source.primary_flow);
  const directComposerUi = resolveObject(source.composer_ui);
  const composerUi = Object.keys(directComposerUi).length > 0
    ? directComposerUi
    : resolveObject(primaryFlow.composer_ui);
  const directChooser = resolveObject(composerUi.composer_chooser);
  const chooser = Object.keys(directChooser).length > 0
    ? directChooser
    : resolveObject(composerUi.chooser_state);
  return [
    String(chooser.source ?? '').trim() === 'authoritative',
    String(chooser.kind ?? '').trim() === 'question',
    chooser.visible === true,
    chooser.requires_explicit_user_action === true,
  ].every(Boolean);
}

function normalizeTraceCategory(category = '') {
  const normalized = String(category || '').trim();
  if (
    normalized === 'routing'
    || normalized === 'orchestration'
    || normalized === 'execution'
    || normalized === 'capability'
    || normalized === 'workspace'
  ) {
    return normalized;
  }
  return 'orchestration';
}

function buildTraceGroups(processTrace = []) {
  return processTrace.reduce((acc, trace) => {
    const category = String(trace?.category || '').trim();
    if (!category) {
      return acc;
    }
    const normalizedCategory = normalizeTraceCategory(category);
    return {
      ...acc,
      [normalizedCategory]: [...acc[normalizedCategory], trace],
    };
  }, {
    routing: [],
    orchestration: [],
    execution: [],
    capability: [],
    workspace: [],
  });
}

export function buildSystemProcessViewModel(item) {
  const record = resolveObject(item);
  const payload = resolveObject(record.payload);
  const currentStage = firstTruthy(String(payload.current_stage ?? '').trim(), 'intent_detecting');
  const stageTransition = resolveObject(payload.stage_transition);
  const decisionBasis = resolveArray(payload.decision_basis);
  const blocking = resolveObject(payload.blocking);
  const contentPolicy = resolveObject(payload.content_policy);
  const detectedIntent = resolveObject(payload.detected_intent);
  const confirmedUpdates = resolveArray(payload.confirmed_updates);
  const openFields = resolveArray(payload.open_fields);
  const nextAction = resolveObject(payload.next_action);
  const intakeUnderstanding = resolveObject(payload.intake_understanding);
  const chooserOwnsQuestion = isAuthoritativeQuestionComposerChooser(payload);
  const currentQuestion = resolveObject(conditionalValue(!chooserOwnsQuestion, payload.current_question));
  const currentQuestionFieldKey = String(currentQuestion.field_key ?? '').trim();
  const currentQuestionText = String(currentQuestion.question_text ?? '').trim();
  const currentStageLabel = String(firstTruthy(payload.current_stage_label, payload.stage_label) ?? '').trim();
  const directionSummary = String(firstTruthy(payload.direction_summary, intakeUnderstanding.summary) ?? '').trim();
  const questionWhyNow = String(currentQuestion.why_now ?? '').trim();
  const candidateGapLabels = resolveArray(intakeUnderstanding.candidate_information_gaps)
      .map((gap) => {
        const gapRecord = resolveObject(gap);
        return String(firstTruthy(gapRecord.label, gapRecord.field_label) ?? '').trim();
      })
      .filter(Boolean);
  const openFieldLabels = openFields
    .map((field) => {
      const fieldRecord = resolveObject(field);
      return String(firstTruthy(fieldRecord.label, fieldRecord.field_label) ?? '').trim();
    })
    .filter(Boolean);
  const confirmedFieldKeys = confirmedUpdates
    .map((field) => {
      const fieldRecord = resolveObject(field);
      return String(firstTruthy(fieldRecord.label, fieldRecord.field_label) ?? '').trim();
    })
    .filter(Boolean);
  const confirmedSummary = conditionalValue(confirmedFieldKeys.length > 0, confirmedFieldKeys.join('、'), '');
  const pendingSummary = conditionalValue(candidateGapLabels.length > 0, candidateGapLabels.join('、'), openFieldLabels.join('、'));
  const blockingReason = String(blocking.reason ?? '').trim();
  const missingFieldText = conditionalValue(openFieldLabels.length > 0, openFieldLabels.join('、'), '');
  const processTrace = resolveArray(record.processTrace);
  const searchActions = resolveArray(payload.search_actions);
  const traceGroups = buildTraceGroups(processTrace);
  const capabilityLinesFromStatus = searchActions.map((action, index) => {
    const actionRecord = resolveObject(action);
    const actionName = String(firstTruthy(actionRecord.action, 'unknown')).trim();
    const resultCount = Number(actionRecord.result_count) || 0;
    const queries = resolveArray(actionRecord.queries);
    return `#${index + 1} ${actionName} · 查询=${queries.length} · 命中=${resultCount}`;
  });
  const totalTraceCount = (
    processTrace.length
    + capabilityLinesFromStatus.length
    + Number(decisionBasis.length > 0)
  );

  return {
    blocking,
    blockingReason,
    capabilityLinesFromStatus,
    confirmedFieldKeys,
    confirmedUpdates,
    contentPolicy,
    currentQuestionFieldKey,
    currentQuestionText,
    currentStage,
    decisionBasis,
    detectedIntent,
    missingFieldText,
    nextAction,
    openFieldLabels,
    openFields,
    candidateGapLabels,
    confirmedSummary,
    currentStageLabel,
    stageTransition,
    directionSummary,
    pendingSummary,
    intakeUnderstanding,
    questionWhyNow,
    totalTraceCount,
    traceGroups,
  };
}
